import { useCallback, useEffect, useReducer, useRef } from 'react';

import { di } from 'react-magnetic-di';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import type { CreationContextParams } from '@atlassian/conversation-assistant-external-context/types';
import {
	type Agent,
	type FileUploadResponseItem,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { useUIStore } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { ROVO_AGENT_UUID } from '@atlassian/conversation-assistant-utils/default-agent';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import { ROVO_INLINE_INPUT_BOX_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-inline-input-box-client';
import { InlineInputBoxAbortMessageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/inline-input-box-abort-message-command';
import { InlineInputBoxAbortedMessageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/inline-input-box-aborted-message-command';
import { InlineInputBoxErrorMessageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/inline-input-box-error-message-command';
import { InlineInputBoxFinalResponseMessageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/inline-input-box-final-response-message-command';
import {
	InlineInputBoxSendMessageCommand,
	type InlineInputBoxSendMessageCommandProperties,
} from '@atlassian/rovo-content-bridge-api-commands/commands/inline-input-box-send-message-command';
import { InlineInputBoxTraceMessageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/inline-input-box-trace-message-command';
import type { CommandPropertiesWithKnownSender } from '@atlassian/rovo-content-bridge-api/command';
import { useRovoContentBridgeAPI } from '@atlassian/rovo-content-bridge-api/hooks';

import { fetchRovoChatAgentIdentityAccountId } from '../../common/utils/rovo-chat-agent/fetch-rovo-chat-agent-identity';
import type { RovoApp } from '../chat-mode-store/types';

type InlineChatControllerOptions = {
	source: string;
	productKey: string;
	siteId?: string;
	onAbort: ({ streamEnded }: { streamEnded: boolean }) => void;
	/**
	 * Called when an inline stream error should hand off to side chat (openChatOnError).
	 * Falls back to calling onAbort when onError is not passed.
	 */
	onError?: ({ streamEnded }: { streamEnded: boolean }) => void;
	onComplete: () => void;
	onSubmit?: (cancel: () => void) => void;
	onFollow?: () => void;
	onCancel?: () => void;
	autoClose?: boolean;
	objectId?: string | number;
	/**
	 * When true, the side chat will open if Rovo encounters an error during the inline edit.
	 */
	openChatOnError?: boolean;
	/**
	 * Optional creation context parameters passed through to the backend for
	 * experience-based agent routing. Consumers should set this to provide the
	 * appropriate experience and content types for their surface (e.g. page,
	 * whiteboard, database).
	 */
	creationContextParams?: CreationContextParams;
	/**
	 * Optional callback that allows the consumer to intercept the submit before it is sent
	 * to the Rovo bridge. If this callback returns `true`, the bridge send is skipped and
	 * the inline prompt is closed. Used by the SmartLink Rovo button to open the Rovo chat
	 * sidebar instead of performing an AI inline edit.
	 */
	onBeforeSubmit?: (prompt: DocNode) => boolean;
	/** Runs before the prompt auto-closes after handoff to an already-open Rovo Chat. */
	onSubmitAutoClose?: () => void;
	/** Returns the editor-owned token used to correlate terminal stream events. */
	getInlineRovoEditAttemptId?: () => string | undefined;
	/**
	 * Optional callback that allows the consumer to control when the transition to 'complete' mode happens.
	 * If provided, it will be called with a `complete()` function that the consumer must call to trigger the transition.
	 * If not provided, the transition happens immediately.
	 */
	onBeforeComplete?: (complete: () => void) => void;
	/**
	 * Keeps the inline prompt mounted but visually hidden after the final response.
	 * Useful for host surfaces that render their own post-completion affordance.
	 */
	hideAfterComplete?: boolean;
	/**
	 * Routes the default Rovo option in an inline agent selector through Rovo Chat
	 * with an explicit agent identity, allowing a matching conversation to be reused.
	 */
	routeDefaultRovoThroughChat?: boolean;
};

type InlineChatMode =
	| 'editing'
	| 'thinking'
	| 'error'
	| 'warning'
	| 'success'
	| 'hidden'
	| 'complete-hidden';

export type InlineChatError = {
	content: string;
	status_code?: number;
	message_template?: string;
};

export type InlineChatTrace = {
	message_template: string;
	action_key?: string;
};

type InlineChatState = {
	mode: InlineChatMode;
	/**
	 * When the inline prompt is hidden because Rovo Chat opened, the previous
	 * mode is captured here so we can restore it when chat closes.
	 */
	modeBeforeHide: InlineChatMode | null;
	latestTrace: InlineChatTrace | null;
	isCancelling: boolean;
	error: InlineChatError | null;
	agent: Agent | null;
};

const initialState: InlineChatState = {
	mode: 'editing',
	modeBeforeHide: null,
	latestTrace: null,
	isCancelling: false,
	error: null,
	agent: null,
};

type ReducerAction =
	| {
			type: 'submit';
	  }
	| {
			type: 'trace';
			latestTrace: InlineChatTrace;
	  }
	| {
			type: 'cancel';
	  }
	| {
			type: 'keep';
	  }
	| {
			type: 'abort';
	  }
	| {
			type: 'complete';
			hideAfterComplete?: boolean;
	  }
	| {
			type: 'error';
			error: InlineChatError;
	  }
	| {
			type: 'success';
	  }
	| {
			type: 'set-agent';
			agent: Agent | null;
	  }
	| {
			/**
			 * Rovo Chat mounted. Hide the inline prompt without tearing down the
			 * in-flight stream. The current `mode` is captured so it can be restored.
			 */
			type: 'chat-mounted';
	  }
	| {
			/**
			 * Rovo Chat unmounted. Restore the inline prompt to whatever `mode` was
			 * captured before it was hidden.
			 */
			type: 'chat-unmounted';
	  }
	| {
			/**
			 * The turn was aborted outside the inline prompt (e.g. Stop in Rovo Chat).
			 * Clears `modeBeforeHide` so closing Rovo Chat does not restore 'thinking'.
			 */
			type: 'stream-aborted';
	  };

const isAwaitingTurn = ({
	mode,
	modeBeforeHide,
}: Pick<InlineChatState, 'mode' | 'modeBeforeHide'>): boolean =>
	mode === 'thinking' || (mode === 'hidden' && modeBeforeHide === 'thinking');

const reducer = (state: InlineChatState, action: ReducerAction): InlineChatState => {
	switch (action.type) {
		case 'submit':
			return { ...state, mode: 'thinking', latestTrace: null, error: null };
		case 'trace':
			return { ...state, latestTrace: action.latestTrace };
		case 'cancel':
			return { ...state, isCancelling: true };
		case 'keep':
			return { ...state, isCancelling: false };
		case 'abort':
			return { ...state, mode: 'editing', isCancelling: false, latestTrace: null, error: null };
		case 'complete':
			return {
				...state,
				mode: action.hideAfterComplete ? 'complete-hidden' : 'editing',
				latestTrace: null,
				error: null,
			};
		case 'error':
			return { ...state, mode: 'error', latestTrace: null, error: action.error };
		case 'success':
			return { ...state, mode: 'success', latestTrace: null, error: null };
		case 'set-agent':
			return { ...state, agent: action.agent };
		case 'chat-mounted':
			// Already paused — do nothing. Preserve the captured modeBeforeHide.
			if (state.mode === 'hidden') {
				return state;
			}
			return { ...state, mode: 'hidden', modeBeforeHide: state.mode };
		case 'chat-unmounted':
			// Not paused — nothing to restore.
			if (state.mode !== 'hidden') {
				return state;
			}
			return {
				...state,
				mode: state.modeBeforeHide ?? 'editing',
				modeBeforeHide: null,
			};
		case 'stream-aborted':
			if (!isAwaitingTurn(state)) {
				return state;
			}
			return {
				...state,
				mode: 'editing',
				modeBeforeHide: null,
				isCancelling: false,
				latestTrace: null,
				error: null,
			};
	}
};

type PromptSubmitParams = {
	prompt: DocNode;
	deepResearchEnabled: boolean;
	webSearchEnabled: boolean;
	appFilters?: RovoApp[];
	files?: FileUploadResponseItem[];
	addCurrentPageAsContextUserPreference?: boolean;
};

type SendMessageParams =
	CommandPropertiesWithKnownSender<InlineInputBoxSendMessageCommandProperties>;

export const useInlineChatController = ({
	source,
	productKey,
	siteId,
	onAbort,
	onError,
	onSubmit,
	onComplete,
	onCancel,
	autoClose,
	objectId,
	openChatOnError,
	creationContextParams,
	onBeforeSubmit,
	onSubmitAutoClose,
	getInlineRovoEditAttemptId,
	onBeforeComplete,
	hideAfterComplete,
	routeDefaultRovoThroughChat,
}: InlineChatControllerOptions): {
	mode: InlineChatMode;
	isCancelling: boolean;
	latestTrace: InlineChatTrace | null;
	error: InlineChatError | null;
	submit: (params: PromptSubmitParams) => void;
	resubmit: () => void;
	cancel: (options?: { skipWarning?: boolean }) => void;
	keep: () => void;
	abort: (endStream?: boolean) => void;
	autoClose: boolean;
	agent: Agent | null;
	setAgent: (agent: Agent | null) => void;
} => {
	di(usePublish, useRovoContentBridgeAPI);
	const publish = usePublish('ai-mate');
	const [{ mode, modeBeforeHide, isCancelling, latestTrace, error, agent }, dispatch] = useReducer(
		reducer,
		initialState,
	);

	const { mounted: isChatMounted } = useUIStore();

	const lastSubmittedInput = useRef<SendMessageParams>();

	const { ready, send } = useRovoContentBridgeAPI(ROVO_INLINE_INPUT_BOX_CLIENT_ID, (on) => {
		on(InlineInputBoxTraceMessageCommand, (latestTrace) => {
			dispatch({ type: 'trace', latestTrace });
		});

		on(InlineInputBoxFinalResponseMessageCommand, () => {
			const complete = () => {
				dispatch({ type: 'complete', hideAfterComplete });
				onComplete();
			};

			if (onBeforeComplete) {
				onBeforeComplete(complete);
			} else {
				complete();
			}
		});

		on(
			InlineInputBoxErrorMessageCommand,
			({ error, openChatOnError: shouldOpenChat, conversationId }) => {
				if (shouldOpenChat && conversationId) {
					// Abort the inline prompt first (resets mode to 'editing') so that
					// the auto-close effect does not fight with the Side Chat opening.
					dispatch({ type: 'abort' });
					// Error handoff is not a user cancel: prefer onError so hosts can
					// tear down UI without aborting remix experiences.
					if (onError) {
						onError({ streamEnded: false });
					} else {
						onAbort({ streamEnded: false });
					}

					// Then open the Side Chat to display the error
					publish({
						type: 'chat-open',
						source: 'input-prompt-box',
						data: {
							channelId: conversationId,
						},
					});
				} else {
					dispatch({ type: 'error', error: error.message });
				}
			},
		);

		on(InlineInputBoxAbortedMessageCommand, ({ inlineRovoEditAttemptId }) => {
			// Ignore if the prompt is not waiting on this turn: it was aborted from the inline
			// prompt itself (Rovo is echoing the abort back), or the abort is for an earlier turn.
			// The experiment is checked last so exposure fires in both cohorts only for eligible aborts.
			if (
				!isAwaitingTurn({ mode, modeBeforeHide }) ||
				inlineRovoEditAttemptId !== lastSubmittedInput.current?.inlineRovoEditAttemptId ||
				!isExperimentEnabled('cc_ce_mau_inline_rovo_edit_sync_abort')
			) {
				return;
			}
			dispatch({ type: 'stream-aborted' });
			// The stream has already ended, so do not send an abort back to Rovo Chat.
			onAbort({ streamEnded: true });
		});
	});

	const isCancelledRef = useRef(false);

	useEffect(() => {
		ready();
	}, [ready]);

	useEffect(() => {
		// Pre-fetch Rovo's identity while the inline prompt is active.
		if (!isExperimentEnabled('platform_editor_mention_rovo') || !siteId || !productKey) {
			return;
		}

		void fetchRovoChatAgentIdentityAccountId({
			cloudId: siteId,
			product: productKey,
		});
	}, [productKey, siteId]);

	const abort = useCallback(
		(endStream: boolean = false): void => {
			isCancelledRef.current = true;
			if (endStream && mode === 'thinking') {
				send(InlineInputBoxAbortMessageCommand, {
					receiverId: ROVO_CHAT_CLIENT_ID,
				});
			}
			dispatch({ type: 'abort' });
			onAbort({ streamEnded: endStream });
		},
		[mode, send, onAbort],
	);

	// When `cc-maui-phase-3` is on, hide the inline prompt while keeping the
	// in-flight stream alive when Rovo Chat opens, and restore it when Rovo Chat
	// closes. Otherwise fall back to the legacy auto-close behaviour, which fully
	// aborts the prompt when chat opens.
	useEffect(() => {
		if (fg('cc-maui-phase-3')) {
			if (isChatMounted && mode !== 'editing' && mode !== 'hidden' && mode !== 'complete-hidden') {
				dispatch({ type: 'chat-mounted' });
			} else if (!isChatMounted && mode === 'hidden') {
				dispatch({ type: 'chat-unmounted' });
			}
			return;
		}
		if (
			isChatMounted &&
			mode !== 'editing' &&
			mode !== 'complete-hidden' &&
			fg('platform_inline_rovo_auto_close')
		) {
			abort();
		}
	}, [isChatMounted, mode, abort]);

	const submit = (params: PromptSubmitParams): void => {
		isCancelledRef.current = false;
		if (
			!isExperimentEnabled('platform_editor_mention_rovo') ||
			agent?.id ||
			!siteId ||
			!productKey
		) {
			submitPrompt(params);
			return;
		}

		void submitPromptWithIdentity(params, siteId, productKey);
	};

	const submitPromptWithIdentity = async (
		params: PromptSubmitParams,
		cloudId: string,
		product: string,
	): Promise<void> => {
		const agentIdentityAccountId = await fetchRovoChatAgentIdentityAccountId({ cloudId, product });
		if (isCancelledRef.current) {
			return;
		}
		submitPrompt(params, agentIdentityAccountId);
	};

	const submitPrompt = (params: PromptSubmitParams, agentIdentityAccountId?: string): void => {
		const inlineAgentEditorContextEnabled = isExperimentEnabled(
			'platform_inline_rovo_agent_editor_context',
		);
		if (agent?.id) {
			// Always start a new conversation and open the chat when submitting with an agent
			publish({
				type: 'chat-new',
				source: 'ai-mate',
				data: {
					dialogues: [],
					prompt: params.prompt,
					agentId: agent.id,
					...(agent.identity_account_id && {
						agentIdentityAccountId: agent.identity_account_id,
					}),
					...(inlineAgentEditorContextEnabled && {
						contentId: objectId,
						creationContextParams,
						mode: {
							useCurrentPageContext: params.addCurrentPageAsContextUserPreference,
						},
					}),
				},
			});

			// Immediately exit the inline prompt as the sidebar chat should take over
			abort();
			return;
		}
		// Allow the consumer to intercept the submit and handle it differently
		// (e.g. open the Rovo sidebar chat instead of performing an inline AI edit).
		if (onBeforeSubmit?.(params.prompt)) {
			abort();
			return;
		}
		const input = {
			...params,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			prompt: params.prompt as any, // TODO: DocNode isn't compatible with the bridge's SerializableValue
			productKey,
			source,
			receiverId: ROVO_CHAT_CLIENT_ID,
			contentId: objectId,
			agentId: agent?.id ?? (routeDefaultRovoThroughChat ? ROVO_AGENT_UUID : undefined),
			reuseMatchingConversation: routeDefaultRovoThroughChat ?? false,
			...(agentIdentityAccountId ? { agentIdentityAccountId } : {}),
			openChatOnError,
			creationContextParams,
			inlineRovoEditAttemptId: getInlineRovoEditAttemptId?.(),
		};

		lastSubmittedInput.current = input;
		send(InlineInputBoxSendMessageCommand, input);

		if (isChatMounted && fg('platform_inline_rovo_auto_close')) {
			// If the side nav is already open, let the chat take over and close the inline prompt
			onSubmitAutoClose?.();
			abort();
		} else {
			// Keep the inline prompt open if the side nav isn't open yet
			dispatch({ type: 'submit' });
			onSubmit?.(() => {
				// Pass skipWarning since this callback is used by global escape handlers during thinking state
				cancelRef.current({ skipWarning: true });
			});
		}
	};

	const resubmit = (): void => {
		if (lastSubmittedInput.current) {
			send(InlineInputBoxSendMessageCommand, lastSubmittedInput.current);
			dispatch({ type: 'submit' });
		}
	};

	const keep = useCallback((): void => {
		dispatch({ type: 'keep' });
	}, []);

	const cancel = useCallback(
		(options?: { skipWarning?: boolean }): void => {
			if (!onCancel || mode === 'editing') {
				abort(true);
			} else if (isCancelling) {
				keep();
			} else if (options?.skipWarning && mode === 'thinking') {
				// When skipWarning is true and in thinking mode, abort immediately without showing warning
				abort(true);
			} else {
				dispatch({ type: 'cancel' });
				onCancel();
			}
		},
		[onCancel, mode, isCancelling, abort, keep],
	);

	const cancelRef = useRef(cancel);
	useEffect(() => {
		cancelRef.current = cancel;
	}, [cancel]);

	const setAgent = (agent: Agent | null): void => {
		dispatch({ type: 'set-agent', agent });
	};

	return {
		mode,
		isCancelling,
		latestTrace,
		error,
		submit,
		resubmit,
		cancel,
		keep,
		abort,
		autoClose: autoClose ?? true,
		agent,
		setAgent,
	};
};
